import {
  BRAND_FILTER_QUERY_KEY,
  REGION_FILTER_QUERY_KEY,
} from "staticConfiguration";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ErrorComponent } from "core/components/ErrorComponent";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import type { TFunction } from "core/entities";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useCentrixApiInfinite } from "core/hooks/useCentrixApiInfinite";
import { useLocale } from "core/hooks/useLocale";
import { usePageLogging } from "core/hooks/usePageLogging";
import { useRegion } from "core/hooks/useRegion";
import { getBrand } from "core/utils/getBrand";
import { parseDateOrNull } from "core/utils/parseDateOrNull";
import { FavoriteButton } from "features/MyLinks/components";
import type { TemporalFilterType } from "features/NewsEvents/entities";
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateTemporalFilterTypesToDateRange } from "../../utils/timeFiltersToStartEndDates";
import {
  DateRangeTypeToggleButtons,
  type DateRangeTypeToggleButtonsProps,
} from "./DateRangeTypeToggleButtons";
import {
  CATEGORY_FILTER_QUERY_KEY,
  EventsArticlesFilters,
  TYPES_FILTER_QUERY_KEY,
} from "./EventsArticlesFilters";
import { EventList } from "./EventsList";

const SINGLE_SELECT_DATE_FILTER_TYPES: TemporalFilterType[] = [
  "past",
  "custom",
];
const DEFAULT_TEMPORAL_FILTERS: TemporalFilterType[] = ["current", "future"];

function Wrapper({ t, children }: { t: TFunction; children: ReactNode }) {
  return (
    <PageContainer maxWidth="xl">
      <PageHeader
        title={t("pageTitle")}
        TitleSlot={
          <FavoriteButton
            itemId="all-events"
            itemType="Page"
            title={t("pageTitle")}
          />
        }
      />
      {children}
    </PageContainer>
  );
}

export function AllEventsPage() {
  usePageLogging({
    itemType: "Page",
    itemId: "all-news",
    componentName: "AllEventsPage",
  });
  const locale = useLocale();
  const { isMobile } = useBreakpoints();
  const defaultBrand = getBrand();
  const defaultRegion = useRegion();
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [forceDefaultBrandRegion, setForceDefaultBrandRegion] = useQueryState(
    "force-default-brand-region",
    parseAsBoolean
  );
  const [selectedBrands, setSelectedBrands] = useQueryState(
    BRAND_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );
  const [selectedRegions, setSelectedRegions] = useQueryState(
    REGION_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );

  const [selectedTags] = useQueryState("tags", parseAsArrayOf(parseAsString));
  const [selectedCategories, setSelectedCategories] = useQueryState(
    CATEGORY_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );
  const [selectedTypes, setSelectedTypes] = useQueryState(
    TYPES_FILTER_QUERY_KEY,
    parseAsArrayOf(parseAsString)
  );
  const [sort, setSort] = useQueryStates({
    sortDirection: parseAsStringEnum(["asc", "desc"]).withDefault("desc"),
    sortField: parseAsString.withDefault("startDate"),
  });

  const [dateFilters, setDateFilters] = useQueryStates({
    temporalFilterType: parseAsArrayOf<TemporalFilterType>(
      parseAsStringEnum(["past", "current", "future", "custom"])
    ).withDefault([]),
    startDateMax: parseAsIsoDateTime,
    startDateMin: parseAsIsoDateTime,
    endDateMax: parseAsIsoDateTime,
    endDateMin: parseAsIsoDateTime,
  });

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isCustomDateFiltering =
    dateFilters.temporalFilterType.includes("custom");

  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:AllEventsPage",
  });

  const onTemporalFilterTypeSelect: DateRangeTypeToggleButtonsProps["onChange"] =
    useCallback(
      (value: TemporalFilterType[]) => {
        const latestSelectedValue = value.at(-1);
        const newValues = SINGLE_SELECT_DATE_FILTER_TYPES.includes(
          latestSelectedValue!
        )
          ? [latestSelectedValue!]
          : value.filter(
              (val) => !SINGLE_SELECT_DATE_FILTER_TYPES.includes(val)
            );
        if (latestSelectedValue === "custom") {
          setIsExpanded(true);
        }
        const newFilters = translateTemporalFilterTypesToDateRange(newValues);
        setDateFilters({
          temporalFilterType: newValues,
          startDateMin: parseDateOrNull(newFilters.startDateMin),
          startDateMax: parseDateOrNull(newFilters.startDateMax),
          endDateMin: parseDateOrNull(newFilters.endDateMin),
          endDateMax: parseDateOrNull(newFilters.endDateMax),
        });
        setSort({
          sortField: newFilters.sortField,
          sortDirection: newFilters.sortDirection,
        });
      },
      [setDateFilters, setSort]
    );

  useEffect(() => {
    if (!isFirstRender) {
      return;
    }
    if (selectedBrands === null) {
      setSelectedBrands([defaultBrand.brandCode]);
    }
    if (selectedRegions === null) {
      setSelectedRegions([defaultRegion]);
    }
    // setTime on first render
    setDateFilters((current) => {
      if (current.temporalFilterType.length === 0) {
        // if no temporal filter type set, default to current + future
        const newFilters = translateTemporalFilterTypesToDateRange(
          DEFAULT_TEMPORAL_FILTERS
        );
        return {
          temporalFilterType: DEFAULT_TEMPORAL_FILTERS,
          startDateMin: parseDateOrNull(newFilters.startDateMin),
          startDateMax: parseDateOrNull(newFilters.startDateMax),
          endDateMin: parseDateOrNull(newFilters.endDateMin),
          endDateMax: parseDateOrNull(newFilters.endDateMax),
        };
      }
      return current;
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFirstRender(false);
  }, [
    defaultBrand.brandCode,
    defaultRegion,
    isFirstRender,
    selectedBrands,
    selectedRegions,
    setDateFilters,
    setSelectedBrands,
    setSelectedRegions,
  ]);

  useEffect(() => {
    if (forceDefaultBrandRegion) {
      setSelectedBrands([defaultBrand.brandCode]);
      setSelectedRegions([defaultRegion]);
      setForceDefaultBrandRegion(null);
    }
  }, [
    defaultBrand.brandCode,
    defaultRegion,
    forceDefaultBrandRegion,
    setForceDefaultBrandRegion,
    setSelectedBrands,
    setSelectedRegions,
  ]);

  const {
    items,
    isLoadingInitialData,
    isError,
    loadMore,
    isLoadingMore,
    totalCount,
    currentCount,
  } = useCentrixApiInfinite({
    path: "/api/news-events/articles-v2/Events/public",
    maxResultCount: 10,
    parameters: {
      query: {
        Locale: locale ?? "",
        Brands: selectedBrands ?? [],
        Regions: selectedRegions ?? [],
        Categories: selectedCategories ?? [],
        Types: selectedTypes ?? [],
        Tags: selectedTags ?? [],
        StartDateMax: dateFilters.startDateMax?.toISOString(),
        StartDateMin: dateFilters.startDateMin?.toISOString(),
        EndDateMax: dateFilters.endDateMax?.toISOString(),
        EndDateMin: dateFilters.endDateMin?.toISOString(),
        Sorting: `${sort.sortField} ${sort.sortDirection}`,
      },
    },
  });

  const Filters = (
    <>
      <EventsArticlesFilters
        expanded={isExpanded}
        setExpanded={setIsExpanded}
        showDateRange={isCustomDateFiltering}
        sx={{ mb: 4 }}
      />
      <Stack direction="row" alignItems="center" flexWrap="wrap">
        <Typography variant="overline" px={2} minWidth="150px">
          {t("sectionTitle", { currentCount, totalCount })}
        </Typography>
        <DateRangeTypeToggleButtons
          isMobile={isMobile}
          t={t}
          onChange={onTemporalFilterTypeSelect}
          value={dateFilters.temporalFilterType}
        />
      </Stack>
      <Divider sx={{ mb: 3 }} />
    </>
  );
  if (isError) {
    return (
      <Wrapper t={t}>
        {Filters}
        <ErrorComponent apiError={isError} />
      </Wrapper>
    );
  }
  if (isLoadingInitialData) {
    return (
      <Wrapper t={t}>
        {Filters}
        <LinearProgress />
      </Wrapper>
    );
  }

  return (
    <Wrapper t={t}>
      {Filters}
      <EventList
        items={items}
        t={t}
        onArticleTypeClick={(typeId) => {
          setSelectedTypes([typeId]);
        }}
        onCategoryClick={(categoryId) => {
          setSelectedCategories([categoryId]);
        }}
        onIntersection={loadMore}
      />
      {isLoadingMore && <LinearProgress />}
    </Wrapper>
  );
}
